import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CheckCircle, XCircle, Clock, Search, Eye, Award, FileText, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  submitted: { bg: 'bg-blue-100 text-blue-700', label: 'Submitted' },
  under_review: { bg: 'bg-amber-100 text-amber-700', label: 'Under Review' },
  approved: { bg: 'bg-green-100 text-green-700', label: 'Approved' },
  rejected: { bg: 'bg-red-100 text-red-700', label: 'Rejected' },
};

const levelLabels = {
  level_1: 'Level 1 — CPD Recognized',
  level_2: 'Level 2 — CPD Quality Assured',
  level_3: 'Level 3 — Advanced CPD Recognized',
};

export default function CPDRecognitionTab({ token }) {
  const [applications, setApplications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [view, setView] = useState('applications');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, actsRes] = await Promise.all([
        axios.get(`${API}/cpd-recognition/applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cpd-recognition/activities`),
      ]);
      setApplications(appsRes.data);
      setActivities(actsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const updateStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/cpd-recognition/applications/${appId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Application ${status}`);
      fetchData();
      setSelectedApp(null);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const approveAsActivity = async (app) => {
    try {
      await axios.post(`${API}/cpd-recognition/activities`, {
        organization_name: app.organization_name,
        program_name: app.program_name,
        recognition_level: app.recognition_level,
        cpd_credits: parseInt(app.learning_hours) || 0,
        recognition_period: '12 months',
        status: 'active',
      }, { headers: { Authorization: `Bearer ${token}` } });
      await updateStatus(app._id || app.id, 'approved');
      toast.success('Activity added to recognized directory');
    } catch {
      toast.error('Failed to approve activity');
    }
  };

  const filtered = applications.filter(a =>
    a.organization_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.program_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.email?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: applications.length,
    submitted: applications.filter(a => a.status === 'submitted').length,
    under_review: applications.filter(a => a.status === 'under_review').length,
    approved: applications.filter(a => a.status === 'approved').length,
  };

  return (
    <div data-testid="cpd-recognition-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="imcac-card">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-xl font-bold text-[#1B2A4A]">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Applications</p>
            </div>
          </CardContent>
        </Card>
        <Card className="imcac-card">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-500" />
            <div>
              <p className="text-xl font-bold text-[#1B2A4A]">{stats.submitted + stats.under_review}</p>
              <p className="text-xs text-slate-500">Pending Review</p>
            </div>
          </CardContent>
        </Card>
        <Card className="imcac-card">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-xl font-bold text-[#1B2A4A]">{stats.approved}</p>
              <p className="text-xs text-slate-500">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="imcac-card">
          <CardContent className="p-4 flex items-center gap-3">
            <Award className="h-5 w-5 text-[#C5A572]" />
            <div>
              <p className="text-xl font-bold text-[#1B2A4A]">{activities.length}</p>
              <p className="text-xs text-slate-500">Recognized Activities</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-4">
        <Button size="sm" variant={view === 'applications' ? 'default' : 'outline'} onClick={() => setView('applications')} className="rounded-sm text-xs" data-testid="view-applications-btn">
          Applications ({applications.length})
        </Button>
        <Button size="sm" variant={view === 'activities' ? 'default' : 'outline'} onClick={() => setView('activities')} className="rounded-sm text-xs" data-testid="view-activities-btn">
          Recognized Activities ({activities.length})
        </Button>
      </div>

      {view === 'applications' && (
        <>
          {/* Search */}
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search applications..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-sm text-sm"
              data-testid="search-cpd-apps"
            />
          </div>

          {/* Application Detail Modal */}
          {selectedApp && (
            <Card className="imcac-card mb-4 border-l-4 border-l-[#C5A572]" data-testid="app-detail">
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading">{selectedApp.program_name}</h3>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedApp(null)} className="text-xs">Close</Button>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div><span className="text-slate-500">Organization:</span> <span className="font-medium">{selectedApp.organization_name}</span></div>
                  <div><span className="text-slate-500">Contact:</span> <span className="font-medium">{selectedApp.contact_person}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="font-medium">{selectedApp.email}</span></div>
                  <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{selectedApp.phone || 'N/A'}</span></div>
                  <div><span className="text-slate-500">Website:</span> <span className="font-medium">{selectedApp.website || 'N/A'}</span></div>
                  <div><span className="text-slate-500">Type:</span> <span className="font-medium">{selectedApp.program_type || 'N/A'}</span></div>
                  <div><span className="text-slate-500">Learning Hours:</span> <span className="font-medium">{selectedApp.learning_hours || 'N/A'}</span></div>
                  <div><span className="text-slate-500">Level Sought:</span> <span className="font-medium">{levelLabels[selectedApp.recognition_level] || selectedApp.recognition_level}</span></div>
                </div>
                {selectedApp.program_description && (
                  <div className="mb-3"><span className="text-sm text-slate-500">Description:</span><p className="text-sm mt-1">{selectedApp.program_description}</p></div>
                )}
                {selectedApp.learning_objectives && (
                  <div className="mb-4"><span className="text-sm text-slate-500">Learning Objectives:</span><p className="text-sm mt-1">{selectedApp.learning_objectives}</p></div>
                )}
                <div className="flex gap-2 pt-3 border-t">
                  {selectedApp.status !== 'approved' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={() => approveAsActivity(selectedApp)} data-testid="approve-btn">
                      <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve & Add to Directory
                    </Button>
                  )}
                  {selectedApp.status !== 'under_review' && selectedApp.status !== 'approved' && (
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => updateStatus(selectedApp._id || selectedApp.id, 'under_review')} data-testid="review-btn">
                      <Clock className="h-3.5 w-3.5 mr-1" /> Mark Under Review
                    </Button>
                  )}
                  {selectedApp.status !== 'rejected' && selectedApp.status !== 'approved' && (
                    <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200" onClick={() => updateStatus(selectedApp._id || selectedApp.id, 'rejected')} data-testid="reject-btn">
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Applications List */}
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>{search ? 'No matching applications' : 'No CPD Recognition applications yet'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((app, i) => {
                const style = statusStyles[app.status] || statusStyles.submitted;
                return (
                  <Card key={i} className="imcac-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedApp(app)} data-testid={`cpd-app-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-[#1B2A4A] text-sm">{app.program_name}</h4>
                            <Badge className={`${style.bg} text-xs`}>{style.label}</Badge>
                          </div>
                          <p className="text-xs text-slate-500">{app.organization_name} — {app.email}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {levelLabels[app.recognition_level]} | {app.learning_hours ? `${app.learning_hours} hours` : 'Hours TBD'} | {new Date(app.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Eye className="h-4 w-4 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {view === 'activities' && (
        <>
          {activities.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Award className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No recognized activities yet</p>
              <p className="text-sm mt-1">Approve CPD applications to add them to the directory.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="activities-admin-table">
                <thead>
                  <tr className="bg-[#1B2A4A] text-white">
                    <th className="p-3 text-left font-medium">Organization</th>
                    <th className="p-3 text-left font-medium">Program</th>
                    <th className="p-3 text-left font-medium">Level</th>
                    <th className="p-3 text-left font-medium">Credits</th>
                    <th className="p-3 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((act, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 font-medium">{act.organization_name}</td>
                      <td className="p-3">{act.program_name}</td>
                      <td className="p-3"><Badge className="text-xs">{levelLabels[act.recognition_level] || act.recognition_level}</Badge></td>
                      <td className="p-3 font-medium">{act.cpd_credits}</td>
                      <td className="p-3"><Badge className="bg-green-100 text-green-700 text-xs capitalize">{act.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
